import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsersService, GetMeResponse, UpdateUserRequest, Organization, OrganizationGroup } from '../../../shared/services/users/users.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { UserRoleService } from '../../../shared/services/user-role/user-role.service';
import { OrganizationGroupService } from '../../../shared/services/organization-group/organization-group.service';
import { CurrentUserContextService } from '../../../shared/services/current-user-context/current-user-context.service';

interface PersonalDraft {
  FIRST_NAME: string;
  LAST_NAME: string;
  EMAIL: string;
}

interface OrgDraft {
  ROLE: string;
  ORGANIZATION_GROUP_ID: number | null;
  ORGANIZATION_ID: number | null;
}

@Component({
  selector: 'app-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss']
})
export class MyAccountComponent implements OnInit, OnDestroy {

  public user: GetMeResponse | null = null;
  public cachedUserDetails: GetMeResponse['USER'] | null = null;
  public cachedUserCachedAt: number | null = null;
  public organizationGroups: OrganizationGroup[] = [];
  public filteredOrgs: Organization[] = [];
  public loadingOrgs = false;
  public loadingOrgGroups = false;
  public accessibleOrgs: Organization[] = [];
  public readonly roleOptions = ['SUPER_ADMIN', 'SUPERVISOR', 'MANAGER', 'ADVISOR'];

  readonly isGroupLevelUser$ = this.userContext.isGroupLevelUser$;
  readonly displayOrganization$ = this.userContext.displayOrganization$;
  readonly displayOrganizationGroup$ = this.userContext.displayOrganizationGroup$;

  public editingPersonal = false;
  public editingOrganization = false;
  public personalDraft: PersonalDraft = { FIRST_NAME: '', LAST_NAME: '', EMAIL: '' };
  public orgDraft: OrgDraft = { ROLE: '', ORGANIZATION_GROUP_ID: null, ORGANIZATION_ID: null };
  public isSaving = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private userService: UsersService,
    private Toaster: ToasterService,
    private authService: AuthService,
    private userRoleService: UserRoleService,
    private orgGroupService: OrganizationGroupService,
    private readonly userContext: CurrentUserContextService,
  ) { }

  get isSuperAdmin(): boolean {
    return this.userRoleService.isSuperAdmin();
  }

  get isSupervisor(): boolean {
    return this.userRoleService.isSupervisor();
  }

  get isManager(): boolean {
    return this.userRoleService.isManager();
  }

  get isAdvisor(): boolean {
    return this.userRoleService.isAdvisor();
  }

  /** Can user edit any organization-related fields (role / org group / org)? */
  get canEditOrganizationSection(): boolean {
    return this.isSuperAdmin;
  }

  private async getCurrentUser() {
    try {
      this.user = await this.userService.getCurrentUser();
      this.loadCacheMeta();
    } catch (error) {
      console.error('Error fetching user:', error);
      this.Toaster.warning('Failed to load user information');
    }
  }

  ngOnInit(): void {
    void this.bootstrap();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async bootstrap(): Promise<void> {
    await this.getCurrentUser();
    this.loadCacheMeta();
    if (this.isSuperAdmin) {
      this.loadOrganizationGroups();
    } else if (this.isSupervisor) {
      this.loadAccessibleOrgs();
    }
  }

  private loadOrganizationGroups(): void {
    this.loadingOrgGroups = true;
    this.orgGroupService.getCachedGroups().pipe(takeUntil(this.destroy$)).subscribe({
      next: (groups) => {
        this.organizationGroups = groups;
        this.loadingOrgGroups = false;
      },
      error: (err) => {
        console.error('Error loading organization groups:', err);
        this.loadingOrgGroups = false;
      },
    });
  }

  private loadOrgsForGroup(groupId: number): void {
    this.loadingOrgs = true;
    this.orgGroupService.getCachedOrgsForGroup(groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (orgs) => {
        this.filteredOrgs = orgs;
        this.loadingOrgs = false;
      },
      error: (err) => {
        console.error('Error loading organizations for group:', err);
        this.filteredOrgs = [];
        this.loadingOrgs = false;
      },
    });
  }

  private loadAccessibleOrgs(): void {
    this.orgGroupService.getCachedAccessibleOrgs().pipe(takeUntil(this.destroy$)).subscribe({
      next: (orgs) => { this.accessibleOrgs = orgs; },
      error: (err) => { console.error('Error loading accessible orgs:', err); },
    });
  }

  switchActiveOrg(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const orgId = parseInt(select.value, 10);
    if (isNaN(orgId)) return;
    this.orgGroupService.switchOrg(orgId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.Toaster.success('Active organization updated'); },
      error: () => { this.Toaster.warning('Failed to switch organization'); },
    });
  }

  public startEditPersonal(): void {
    if (!this.user) return;
    this.personalDraft = {
      FIRST_NAME: this.user.USER.FIRST_NAME ?? '',
      LAST_NAME: this.user.USER.LAST_NAME ?? '',
      EMAIL: this.user.USER.EMAIL ?? '',
    };
    this.editingPersonal = true;
  }

  public cancelEditPersonal(): void {
    this.editingPersonal = false;
  }

  public async savePersonal(): Promise<void> {
    if (!this.user || this.isSaving) return;
    const current = this.user.USER;
    const dirty =
      this.personalDraft.FIRST_NAME !== (current.FIRST_NAME ?? '') ||
      this.personalDraft.LAST_NAME !== (current.LAST_NAME ?? '') ||
      this.personalDraft.EMAIL !== (current.EMAIL ?? '');
    if (!dirty) {
      this.editingPersonal = false;
      return;
    }
    const payload: UpdateUserRequest = {
      FIRST_NAME: this.personalDraft.FIRST_NAME,
      LAST_NAME: this.personalDraft.LAST_NAME,
      EMAIL: this.personalDraft.EMAIL,
      ORGANIZATION_ID: current.ORGANIZATION_ID,
      ROLE: current.ROLE,
    };
    await this.submit(payload, () => { this.editingPersonal = false; });
  }

  public startEditOrganization(): void {
    if (!this.user || !this.canEditOrganizationSection) return;
    const groupId = this.user.ORGANIZATION_GROUP?.GROUP_ID ?? null;
    this.orgDraft = {
      ROLE: this.user.USER.ROLE ?? '',
      ORGANIZATION_GROUP_ID: groupId,
      ORGANIZATION_ID: this.user.USER.ORGANIZATION_ID ?? null,
    };
    this.filteredOrgs = [];
    if (groupId != null) {
      this.loadOrgsForGroup(groupId);
    }
    this.editingOrganization = true;
  }

  public onOrgGroupChange(): void {
    this.orgDraft.ORGANIZATION_ID = null;
    this.filteredOrgs = [];
    if (this.orgDraft.ORGANIZATION_GROUP_ID != null) {
      this.loadOrgsForGroup(this.orgDraft.ORGANIZATION_GROUP_ID);
    }
  }

  public cancelEditOrganization(): void {
    this.editingOrganization = false;
  }

  public async saveOrganization(): Promise<void> {
    if (!this.user || this.isSaving) return;
    if (!this.canEditOrganizationSection) return;
    const current = this.user.USER;
    const roleChanged = this.orgDraft.ROLE !== (current.ROLE ?? '');
    const orgChanged = this.orgDraft.ORGANIZATION_ID !== (current.ORGANIZATION_ID ?? null);
    if (!roleChanged && !orgChanged) {
      this.editingOrganization = false;
      this.Toaster.info('No changes to save');
      return;
    }
    const payload: UpdateUserRequest = {
      FIRST_NAME: current.FIRST_NAME,
      LAST_NAME: current.LAST_NAME,
      EMAIL: current.EMAIL,
      ROLE: this.orgDraft.ROLE,
      ORGANIZATION_ID: this.orgDraft.ORGANIZATION_ID,
    };
    await this.submit(payload, () => { this.editingOrganization = false; });
  }

  private async submit(payload: UpdateUserRequest, onSuccess: () => void): Promise<void> {
    if (!this.user) return;
    this.isSaving = true;
    this.Toaster.info('Saving changes...');
    try {
      await this.userService.updateUser(this.user.USER.USER_ID, payload);
      await this.userService.clearCachedUserAndRefresh();
      await this.getCurrentUser();
      this.loadCacheMeta();
      onSuccess();
      this.Toaster.success('Changes saved successfully');
    } catch (error: unknown) {
      console.error('Error saving changes:', error);
      const message = this.extractErrorMessage(error);
      this.Toaster.warning(message || 'Failed to save changes');
    } finally {
      this.isSaving = false;
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (typeof error === 'object') {
      const err = error as { message?: unknown; error?: { message?: unknown } };
      if (typeof err.message === 'string') return err.message;
      if (err.error && typeof err.error.message === 'string') return err.error.message;
    }
    return '';
  }

  public async logout() {
    await this.authService.logout();
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private loadCacheMeta(): void {
    const all_cached_detail = this.userService.getCachedUserDetails();
    if (all_cached_detail) {
      this.cachedUserDetails = all_cached_detail.USER;
    }
    this.cachedUserCachedAt = this.userService.getCachedUserDetailsCachedAt();
  }

  public async clearCacheAndRefetch(): Promise<void> {
    try {
      await this.userService.clearCachedUserAndRefresh();
      await this.getCurrentUser();
      this.loadCacheMeta();
      this.Toaster.success('Cache cleared and user details re-fetched');
    } catch (error) {
      console.error('Error clearing cache and re-fetching:', error);
      this.Toaster.warning('Failed to refresh user details');
    }
  }

  public formatCachedAt(ts: number | null): string {
    if (!ts) return 'Not cached';
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}
